import type * as React from 'react'

import { Combobox } from '#/shared/ui/combobox'
import type { ComboboxAction, ComboboxOption } from '#/shared/ui/combobox'
import type { CustomerListItem } from '#/shared/api/generated/shipment/models'

import { useComboboxFreeze } from '../model/use-combobox-freeze'
import { useCustomerSearch } from '../model/use-customer-search'

interface CustomerNameComboboxProps {
  value: string
  onValueChange: (value: string) => void
  onCustomerSelect?: (customer: CustomerListItem) => void
  label?: string
  placeholder?: string
  error?: boolean
  className?: string
  action?: ComboboxAction
  searchDisabled?: boolean
  suffix?: React.ReactNode
  onClear?: () => void
}

const ALWAYS_PASS_FILTER = () => true

const EMPTY_LABEL = '—'

function customerLabel(customer: CustomerListItem): string {
  const name = [customer.first_name, customer.last_name]
    .filter(Boolean)
    .join(' ')
  if (name.length > 0) return name
  if (customer.company && customer.company.length > 0) return customer.company
  return EMPTY_LABEL
}

function customerTag(customer: CustomerListItem): string | undefined {
  if (customer.company && customer.company.length > 0) return customer.company
  return undefined
}

export function CustomerNameCombobox({
  value,
  onValueChange,
  onCustomerSelect,
  label,
  placeholder,
  error,
  className,
  action,
  searchDisabled = false,
  suffix,
  onClear,
}: CustomerNameComboboxProps) {
  const freeze = useComboboxFreeze<CustomerListItem>(value)
  const searchEnabled =
    freeze.isUserTyped && !searchDisabled && !freeze.isJustSelected
  const live = useCustomerSearch(value, { enabled: searchEnabled })
  const items = freeze.pickItems(live.items)
  const options: ComboboxOption[] = searchDisabled
    ? []
    : items.map((customer) => ({
        value: customer.id,
        label: customerLabel(customer),
        tag: customerTag(customer),
      }))

  const handleValueChange = (next: string) => {
    freeze.onTyped(next)
    onValueChange(next)
  }

  const handleSelect = (option: ComboboxOption) => {
    freeze.freeze(option.label, items)
    const customer = items.find((it) => it.id === option.value)
    if (customer) onCustomerSelect?.(customer)
  }

  return (
    <Combobox
      labelSize="sm"
      label={label}
      placeholder={placeholder}
      options={options}
      value={value}
      onValueChange={handleValueChange}
      onSelect={handleSelect}
      filter={ALWAYS_PASS_FILTER}
      loading={!searchDisabled && live.isLoading}
      hideEmpty={searchDisabled || live.needsMoreInput || !freeze.isUserTyped}
      hideToggle
      error={error}
      className={className}
      action={searchDisabled ? undefined : action}
      suffix={suffix}
      onClear={onClear}
      clearable
    />
  )
}
